import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useMutateRemoveWarehouse } from 'modules/inventory/api/mutation'
import type { Warehouse } from 'modules/inventory/api/type'
import { useFarmGroup } from 'modules/member/api/selector'
import { useGetCRUDByFeature } from 'modules/user/api/selector'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

import CreateWarehouseDialog from 'modules/inventory/pages/warehouse/CreateWarehouse'

interface Props {
  warehouseId: string
  onClose: () => void
  open: boolean
  warehouseIdItem: Warehouse
  anchorEl: HTMLElement | null
}

const WarehouseTools = ({ warehouseId, onClose, open, warehouseIdItem, anchorEl }: Props) => {
  const { t } = useTranslation(['category'])
  const cruds = useGetCRUDByFeature(FeatureKey.Inventory)
  const { refetch: refetchFarmGroup } = useFarmGroup('', { enabled: false })

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | undefined>(undefined)

  const removeWarehouseMutation = useMutateRemoveWarehouse()

  const { dialog, openDialog: openConfirmDeleteDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (warehouseId) {
        await removeWarehouseMutation
          .mutateAsync(warehouseId, { onSuccess: () => onClose() })
          .finally(() => refetchFarmGroup())
      }
    },
  })

  const handleEditWarehouse = () => {
    setEditingWarehouse(warehouseIdItem)
    setEditDialogOpen(true)
    onClose()
  }

  const toolsMenuItems = [
    {
      icon: <img src="/static/icons/ci_edit.svg" alt="edit" />,
      title: t('category.edit'),
      crudIdx: CRUDAction.Update,
      onClick: handleEditWarehouse,
    },
    {
      icon: <img src="/static/icons/ci_delete.svg" alt="delete" />,
      title: t('category.delete'),
      crudIdx: CRUDAction.Delete,
      onClick: openConfirmDeleteDialog,
    },
  ]

  const getTools = () => {
    if (!cruds) return []
    return toolsMenuItems.filter((m) => m.crudIdx === undefined || cruds[m.crudIdx] === 1)
  }

  return (
    <Paper>
      <Menu
        sx={{ width: '240px' }}
        id={`edit-card-${warehouseId}`}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ 'aria-labelledby': `basic-button-${warehouseId}` }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 6,
            variant: 'elevation',
            sx: { borderRadius: 2 },
          },
        }}
      >
        <MenuList sx={{ py: 0 }}>
          {getTools().map((menuItem, index) => (
            <MenuItem key={index} onClick={menuItem.onClick}>
              <ListItemIcon sx={{ mr: 0 }}>{menuItem.icon}</ListItemIcon>
              <ListItemText>
                <Typography variant="body2">{menuItem.title}</Typography>
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {dialog}
      <CreateWarehouseDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditingWarehouse(undefined) }}
        warehouse={editingWarehouse}
      />
    </Paper>
  )
}

export default WarehouseTools
